import axios from "axios";
import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { useRouter } from "vue-router";
import { useModals } from "@/utils/useModals";
import { useFieldsStore } from "./storeFieldData";
import { useSettingsStore } from "./storeSettings";
import { sendMetrik } from "@/utils/metrika";
import { phoneFormatter } from "@/utils/functions";
import { API_URL } from "@/utils/constans";

export const useRequestsStore = defineStore("request", () => {
	const router = useRouter();
	const storeField = useFieldsStore();
	const storeSettings = useSettingsStore();
	const { responseOk, closeModal, openModal } = useModals();

	const token = ref(localStorage.getItem("token"));
	const setToken = (newToken) => {
		token.value = newToken;
		localStorage.setItem("token", newToken);
	};

	const removeToken = () => {
		token.value = null;
		localStorage.removeItem("token");
	};

	const loaderBtn = ref(false);
	const userData = ref({});
	const responseError = ref("");
	const statusCheckSubscriptions = ref("in_progress");
	const isAuthenticated = computed(() => !!token.value);

	axios.interceptors.response.use(
		(response) => response,
		(error) => {
			if (error.response && error.response.status === 401) {
				removeToken();
				delete axios.defaults.headers.common["Authorization"];
			}
			return Promise.reject(error);
		}
	);

	const setAuthHeader = () => {
		const urlParams = new URLSearchParams(window.location.search);
		const tokenURL = urlParams.get("token");
		if (tokenURL) {
			token.value = tokenURL;
		} else {
			token.value = localStorage.getItem("token");
		}
		if (token.value) {
			axios.defaults.headers.common["Authorization"] = `Bearer ${token.value}`;
		} else {
			delete axios.defaults.headers.common["Authorization"];
		}
	};

	const registration = async () => {
		loaderBtn.value = true;
		const registrationParams = localStorage.getItem("registration_params") || "";

		try {
			const formData = new FormData();

			formData.append("first_name", storeField.fields.firstName.title);
			formData.append("last_name", storeField.fields.lastName.title);
			formData.append("phone_number", storeField.fields.phone.title);
			formData.append("email", storeField.fields.email.title);
			formData.append("password", storeField.fields.passwordRepeat.title);
			formData.append("registration_params", registrationParams);
			formData.append("is_policy", storeField.fields.agreement.title);
			formData.append("is_rules", storeField.fields.rules.title);
			formData.append("agree_to_advertising", storeField.fields.agreeToAdvertising.title);

			const res = await axios.post(`${API_URL}/api/users/registration`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			openModal("registrationEnd");
			// sendMetrik("reachGoal", "registration_succes");
			storeField.updateRegestrationBtn();
		} catch (error) {
			console.error("Error sendRegistration", error);
			responseError.value = error.response?.data?.detail || "Ошибка при отправке данных.";
			console.log("error.response:", error.response);
		} finally {
			loaderBtn.value = false;
		}
	};

	const authorization = async () => {
		loaderBtn.value = true;
		const parameters = {
			email: storeField.fields.email.title,
			password: storeField.fields.password.title,
		};

		try {
			const res = await axios.post(`${API_URL}/api/users/authorization`, parameters);
			setToken(res.data.access);
			setAuthHeader();
			await userInfo();
			closeModal();
			// sendMetrik("reachGoal", "authorization_succes");
			storeField.updateFields("email", "disabledBtn", false);
			storeField.updateFields("password", "disabledBtn", false);
		} catch (error) {
			console.log("error sendAuthorization:", error);
			responseError.value = error.response?.data?.detail || "Ошибка при отправке данных.";
			if (error.response.status === 557) {
				openModal("notVerified");
			}
		} finally {
			loaderBtn.value = false;
		}
	};

	const userInfo = async () => {
		try {
			setAuthHeader();
			const res = await axios.get(`${API_URL}/api/users/me`);
			userData.value = res.data;
			initWidget();
		} catch (error) {
			if (error.response && error.response.status === 401) {
				console.warn("Пользователь не авторизован");
				userData.value = null;
			} else {
				console.error("Error getUserInfo", error);
				throw error;
			}
		}
	};
	
	const checkSubscriptions = async (quizId, userId) => {
		const MAX_RETRIES = 20;
		const RETRY_DELAY = 500; 

		for (let retries = 0; retries < MAX_RETRIES; retries++) {
			try {
				const { data } = await axios.get(
					`${API_URL}/api/quiz/check-subscriptions?quiz=${quizId}&user=${userId}`
				);

				statusCheckSubscriptions.value = data.checking_channels_status;

				if (data?.checking_channels_status !== "in_progress") {
					return data.checking_channels_status;
				}

				await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
			} catch (error) {
				console.error("Ошибка при проверке подписок:", error);
				responseError.value =
					error.response?.data?.detail || "Ошибка при отправке данных.";
				throw error;
			}
		}

		console.warn("Проверка подписок прервана по таймауту");
		return "timeout";
	};

	const quizInfo = async (id) => {
		try {
			const { data } = await axios.get(`${API_URL}/api/quiz/${id}`);
			return data;
		} catch (error) {
			console.log("Error quizInfo", error);
			responseError.value = error.response?.data?.detail || "Ошибка при отправке данных.";
		} finally {
		}
	};

	const quizJoin = async (parameters) => {
		try {

			await axios.post(`${API_URL}/api/quiz/join`, parameters);
			
			await checkSubscriptions(parameters.quiz, parameters.user);
			
		} catch (error) {
			console.error("Error quizJoin", error);
			responseError.value = error.response?.data?.detail || "Ошибка при отправке данных.";
		} finally {
		}
	};

	const getMemberStatus = async (quizId, userId) => {
		try {
			const { data } = await axios.get(`${API_URL}/api/quiz/member-status?quiz=${quizId}&user=${userId}`);
			
			if(data.is_member) {
				await checkSubscriptions(quizId, userId);
				
			} else {
				statusCheckSubscriptions.value = "not_running";
			}
		} catch(error) {
			console.error("Error getMemberStatus", error);
			responseError.value = error.response?.data?.detail || "Ошибка при отправке данных";
		} finally {

		}
	}

	const feedback = async () => {
		loaderBtn.value = true;

		const formData = new FormData();

		if (storeField.fields.filesForPost && storeField.fields.filesForPost.length > 0) {
			for (const file of storeField.fields.filesForPost) {
				formData.append("files", file);
			}
		}

		formData.append("name", storeField.fields.firstName.title);
		formData.append("phone_number", phoneFormatter(storeField.fields.phone.title));
		formData.append("email", storeField.fields.email.title);
		formData.append("text", storeField.fields.message.title);

		try {
			const res = await axios.post(`${API_URL}/api/feedback/send`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			openModal("statusOk");
			responseError.value = "";
			storeField.updateFeedbackBtn();
		} catch (error) {
			responseError.value = error.response?.data?.detail || "Ошибка при отправке данных.";
			console.error("Error sendFeedback", error);
		} finally {
			loaderBtn.value = false;
		}
	};

	return {
		setAuthHeader,
		registration,
		authorization,
		userInfo,
		quizInfo,
		quizJoin,
		getMemberStatus,
		feedback,

		isAuthenticated,
		loaderBtn,
		userData,
		statusCheckSubscriptions,
	};
});
